import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, expand, filter, last, map, Observable, of, take } from 'rxjs';
import { IntegrationModel } from 'src/app/models/api';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FindingThreadDetailsModel } from 'src/app/models/api/finding-thread.model';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { ClientApplicationIconPipe } from 'src/app/pipes/client-application-icon/client-application-icon.pipe';
import { AvatarStylePipe } from 'src/app/pipes/avatar-style/avatar-style.pipe';
import { Severities, SeveritiesCellComponent } from 'src/app/components/severities-cell/severities-cell.component';
import { ExpandableItemComponent } from './expandable-item/expandable-item.component';
import { FindingItemComponent } from './finding-item/finding-item.component';
import { AgentStepModel, AiService, StepType } from 'src/app/models/api/agentStep.model';
import { Label } from 'src/app/labels/englishLabels';
import { LucideAngularModule } from 'lucide-angular';
import { SensitiveDataContentService } from './sensitive-data-content.service';
import { ActivitiesMessagesComponent } from './activities-messages/activities-messages.component';
import { FindingModel } from 'src/app/models/api/finding.model';
import { AiAgentStepsHttpService } from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { ITableColumn } from 'src/app/models/tableColumns/tableColumn.interface';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { FetchAllContentBarComponent } from './fetch-all-content-bar/fetch-all-content-bar.component';
import { CopyButtonComponent } from 'src/app/components/copy-button/copy-button.component';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { AnchorLinkComponent } from 'src/app/components/anchor-button/anchor-link.component';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { AgentInventoryNavigationService } from 'src/app/services/actions/agent-inventory-navigation.service';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { CommonActionsService } from 'src/app/services/actions/common/common-actions.service';
import { RequiredParamsForChatGptEnterpriseDeleteConversationAction } from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { DetectionRuleSeverity } from 'src/app/models/api/finding.model';
import { ActivitySource } from 'src/app/models/api/agentStep.model';

interface FindingThreadViewModel {
  id: string;
  integrationId: string;
  name: string;
  service: AiService;
  clientApplication: string;
  actorNames: string[];
  actorInventoryIds: (string | null)[];
  agentName: string;
  agentInventoryId: string | null;
  findingsBySeverity: Severities;
  firstActivity: Date;
  lastActivity: Date;
  findings: (FindingModel & { severity: Severities })[];
  activities: AgentStepModel[];
  findingsByActivity: Record<AgentStepModel['id'], Severities>;
  findingIdsByActivity: Record<AgentStepModel['id'], string[]>;
}

@Component({
  selector: 'app-finding-thread',
  templateUrl: './enterprise-ai-finding-thread.component.html',
  styleUrls: ['./enterprise-ai-finding-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SensitiveDataContentService],
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    SeveritiesCellComponent,
    ExpandableItemComponent,
    FindingItemComponent,
    LucideAngularModule,
    ActivitiesMessagesComponent,
    MatTooltipModule,
    FetchAllContentBarComponent,
    AvatarModule,
    AvatarGroupModule,
    AvatarStylePipe,
    CopyButtonComponent,
    AnchorLinkComponent,
    DisplayCamelCaseStringPipe,
    ClientApplicationIconPipe,
  ],
  standalone: true,
})
export class EnterpriseAiFindingThreadComponent implements OnInit {
  findingThreadId = input.required<string>();
  sourcePipeline = input<string>();

  public threadFindingsModel = signal<FindingThreadViewModel | null>(null);
  public showNoData = signal(false);
  public selectedIntegration: IntegrationModel | undefined;

  protected visibleFindings = computed(() => {
    const model = this.threadFindingsModel();
    if (!model) return [];
    const getEarliestStepTimestamp = (finding: FindingModel): number => {
      if (!finding.relatedSteps || finding.relatedSteps.length === 0) {
        return new Date(finding.timestamp).getTime();
      }
      const timestamps = finding.relatedSteps.map((step) => new Date(step.timestamp).getTime());
      return Math.min(...timestamps);
    };
    const sortByRelatedStep = (a: FindingModel, b: FindingModel) =>
      getEarliestStepTimestamp(a) - getEarliestStepTimestamp(b);
    const result = model.findings.sort(sortByRelatedStep);
    if (!this.activeStepId()) return result;
    return result.filter((finding) => finding.relatedSteps.some((step) => step.id === this.activeStepId()));
  });

  protected activeStepId = signal<string>('');

  protected withSensitiveData = computed(() => {
    const hasSensitiveData = this.isSensitiveDataPresent(this.threadFindingsModel());
    const notFullyLoaded = !this._sensitiveDataContentService.allSensitiveDataLoaded();
    const hasPartialFailure = this._sensitiveDataContentService.hasPartialFailure();
    return (hasSensitiveData && notFullyLoaded) || hasPartialFailure;
  });
  protected aiServiceIconPath = computed(() => {
    const model = this.threadFindingsModel();
    return model ? getAiServiceLogoPath(model.service) : null;
  });
  protected shouldShowAgentLink = computed<boolean>(() => {
    const model = this.threadFindingsModel();
    if (!model) return false;

    return this._agentInventoryNavigationService.shouldShowAgentLink({
      inventoryId: model.agentInventoryId,
      resourceId: model.findings?.[0]?.relatedSteps?.[0]?.botDetails?.platformInfo?.resourceId,
      service: model.service,
    });
  });

  protected isSensitiveDataLoading = this._sensitiveDataContentService.isLoading;
  protected activitiesMessagesComponent = viewChild(ActivitiesMessagesComponent);

  protected Label = Label;

  private readonly _httpClient = inject(HttpClient);
  private readonly _featureFlagsService = inject(FeatureFlagsService);
  private readonly _sidePanelService = inject(SidePanelService);

  protected isDeleteThreadAvailable = computed(() => {
    const model = this.threadFindingsModel();
    return model?.service === AiService.chatGptEnterprise;
  });

  constructor(
    private readonly _storeService: StoreService,
    private readonly _aiFindingsService: AiFindingsHttpService,
    private readonly _aiActivityHttpService: AiAgentStepsHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _destroyRef: DestroyRef,
    private readonly _sensitiveDataContentService: SensitiveDataContentService,
    private readonly _agentInventoryNavigationService: AgentInventoryNavigationService,
    private readonly _commonActionsService: CommonActionsService
  ) {}

  public ngOnInit() {
    if (
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries) ||
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex)
    ) {
      this._tryLoadMockThread();
    } else {
      this._loadThreadFromApi();
    }
  }

  private _loadThreadFromApi(): void {
    const sourcePipeline = this._isInlineSource() ? ActivitySource.inline : undefined;
    combineLatest([
      this._aiFindingsService.getThread(this.findingThreadId(), sourcePipeline),
      this._fetchAllAgentSteps(),
      this._storeService.integrations$,
    ])
      .pipe(
        take(1),
        filter(([finding]) => {
          if (!finding) {
            console.warn('Finding not found in the server. Not showing any data');
            this.showNoData.set(true);
            return false;
          }
          return true;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: ([finding, activities, integrations]) => {
          this.selectedIntegration = integrations.find((integration) => integration.id === finding.integrationId);
          this.threadFindingsModel.set(this.mapFindingThreadModelToViewModel(finding, activities));
          this.showNoData.set(false);
        },
        error: (error) => {
          if (error.status === 404) this.showNoData.set(true);
          else this._httpErrorHandlerService.handle(error);
        },
      });
  }

  private _tryLoadMockThread(): void {
    type FindingsMap = Record<string, FindingModel>;
    const rsa$ = this._httpClient.get<FindingsMap>('assets/mockData/rsaFindingsMockData.json').pipe(
      map((d) => Object.values(d)),
      take(1)
    );
    const iam$ = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex)
      ? this._httpClient.get<FindingsMap>('assets/mockData/iamDemoFindingsMockData.json').pipe(
          map((d) => Object.values(d)),
          take(1)
        )
      : of([] as FindingModel[]);

    combineLatest([rsa$, iam$])
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ([rsaFindings, iamFindings]) => {
          const all = [...iamFindings, ...rsaFindings];
          const threadFindings = all.filter((f) => f.thread.id === this.findingThreadId());
          if (threadFindings.length === 0) {
            this._loadThreadFromApi();
            return;
          }
          this._initMockThread(threadFindings);
        },
        error: () => this._loadThreadFromApi(),
      });
  }

  private _initMockThread(threadFindings: FindingModel[]): void {
    const first = threadFindings[0];
    const actors = new Set<string>();
    const agents = new Set<string>();
    const sevCounts: Record<string, number> = {};
    const owaspSet = new Set<string>();
    let earliest = new Date(first.timestamp);
    let latest = new Date(first.timestamp);

    for (const f of threadFindings) {
      const sev = f.rule.severity;
      sevCounts[sev] = (sevCounts[sev] ?? 0) + 1;
      if (f.mainActor?.displayName) actors.add(f.mainActor.displayName);
      const agent = f.relatedSteps?.[0]?.botDetails?.displayName;
      if (agent) agents.add(agent);
      (f.rule.owasp ?? []).forEach((o) => owaspSet.add(o));
      const ts = new Date(f.timestamp);
      if (ts < earliest) earliest = ts;
      if (ts > latest) latest = ts;
    }

    this.selectedIntegration = {
      id: first.integration.id,
      name: 'Demo',
    } as IntegrationModel;

    const mockThread: FindingThreadDetailsModel = {
      id: this.findingThreadId(),
      name: [...agents][0] ?? this.findingThreadId(),
      service: first.rule.service as AiService,
      actorNames: [...actors],
      agentNames: [...agents],
      findingsBySeverity: {
        [DetectionRuleSeverity.informational]: sevCounts[DetectionRuleSeverity.informational] ?? 0,
        [DetectionRuleSeverity.low]: sevCounts[DetectionRuleSeverity.low] ?? 0,
        [DetectionRuleSeverity.medium]: sevCounts[DetectionRuleSeverity.medium] ?? 0,
        [DetectionRuleSeverity.high]: sevCounts[DetectionRuleSeverity.high] ?? 0,
        [DetectionRuleSeverity.critical]: sevCounts[DetectionRuleSeverity.critical] ?? 0,
      },
      firstActivity: earliest,
      lastActivity: latest,
      integrationId: first.integration.id,
      findings: threadFindings,
      labels: (first.labelBindings ?? []).map((lb) => lb.label),
    };

    const mockActivities: AgentStepModel[] = threadFindings.flatMap((f) => f.relatedSteps ?? []);
    const stepIds = mockActivities.map((s) => s.id);
    this._sensitiveDataContentService.setMockStepIds(stepIds);

    this.threadFindingsModel.set(this.mapFindingThreadModelToViewModel(mockThread, mockActivities));
    this.showNoData.set(false);
  }

  public scrollToRelatedStep(finding: FindingModel, event: MouseEvent): void {
    event.stopPropagation();
    const step = finding.relatedSteps?.[0];
    if (!step) return;
    this.activitiesMessagesComponent()?.scrollToStep(step.id);
    this._sidePanelService.openAgentStepDetailsFromFinding(step.thread.id, step.id, step);
  }

  public navigateToAgent(): void {
    const model = this.threadFindingsModel();
    if (!model) return;

    this._agentInventoryNavigationService.navigateToAgent({
      inventoryId: model.agentInventoryId,
      resourceId: model.findings?.[0]?.relatedSteps?.[0]?.botDetails?.platformInfo?.resourceId,
      service: model.service,
      integration: this.selectedIntegration,
    });
  }

  public shouldShowActorLink(actorInventoryId: string | null): boolean {
    return this._agentInventoryNavigationService.shouldShowActorLink({
      actorInventoryId,
    });
  }

  public navigateToActor(actorInventoryId: string | null): void {
    if (!actorInventoryId) return;

    this._agentInventoryNavigationService.navigateToActor({
      actorInventoryId,
      integration: this.selectedIntegration,
    });
  }

  public deleteThread(): void {
    const model = this.threadFindingsModel();
    if (!model || !this.selectedIntegration) return;

    const firstActivity = model.activities[0];
    if (!firstActivity) return;

    const params = {
      integrationId: this.selectedIntegration.id,
      stepId: firstActivity.id,
    } as RequiredParamsForChatGptEnterpriseDeleteConversationAction;

    this._commonActionsService
      .chatGptEnterpriseDeleteGptConversationAction(params)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  private _isInlineSource(): boolean {
    return this.sourcePipeline() === 'inline';
  }

  private _fetchAllAgentSteps(): Observable<AgentStepModel[]> {
    const allSteps: AgentStepModel[] = [];

    return this._aiActivityHttpService
      .startGetAgentSteps(
        {
          threadId: [this.findingThreadId()],
          ...(this._isInlineSource() && { source_pipeline: [ActivitySource.inline] }),
        },
        {
          column: {
            sortKey: 'timestamp',
            width: 324,
          } as ITableColumn,
          isDesc: false,
        }
      )
      .pipe(
        expand((response: JsonHalCollection<AgentStepModel>) => {
          allSteps.push(...response._embedded.items);

          if (response._links.next?.href) {
            const continuationToken = this._extractContinuationToken(response._links.next.href);
            return this._aiActivityHttpService.getAgentStepsByContinuationToken(continuationToken);
          }

          return of();
        }),
        last(),
        map(() => allSteps)
      );
  }

  private _extractContinuationToken(nextUrl: string): string {
    const url = new URL(nextUrl, window.location.origin);
    return url.searchParams.get('continuationToken') || '';
  }

  private isSensitiveDataPresent(model: FindingThreadViewModel | null): boolean {
    if (!model) return false;
    return model.activities.some((activity) => {
      if ((activity as unknown as { _inlineMode?: boolean })._inlineMode) {
        return true;
      }

      switch (activity.type) {
        case StepType.aiMessageStep:
        case StepType.userMessageStep:
        case StepType.agentHandoffRequestStep:
        case StepType.agentHandoffResponseStep:
          return true;

        case StepType.ragStep:
          switch (activity.service) {
            case AiService.copilotStudio:
              if (activity.ragQuery) return true;
              break;
            case AiService.vertexAi:
              if (activity.ragQueryAvailable) return true;
              break;
          }
          break;
        case StepType.triggerStep:
          return true;
        case StepType.toolInvocationStep:
          return true;
        case StepType.toolInvocationRequestStep:
        case StepType.toolInvocationResultStep:
        case StepType.toolRequestStep:
        case StepType.toolResponseStep:
          return true;
      }

      return false;
    });
  }

  private mapFindingThreadModelToViewModel(
    thread: FindingThreadDetailsModel,
    activities: AgentStepModel[]
  ): FindingThreadViewModel {
    const clientApplication =
      activities.find((a) => a?.clientContext?.clientApplication)?.clientContext?.clientApplication ?? '';

    const activityTimestamps = activities.map((a) => new Date(a.timestamp).getTime());
    const firstActivity =
      activityTimestamps.length > 0 ? new Date(Math.min(...activityTimestamps)) : thread.firstActivity;
    const lastActivity =
      activityTimestamps.length > 0 ? new Date(Math.max(...activityTimestamps)) : thread.lastActivity;

    const agentInventoryId = activities.find((a) => a.botDetails?.inventoryId)?.botDetails?.inventoryId || null;

    const actorMap = new Map<string, string | null>();
    activities.forEach((activity) => {
      if (activity.mainActor) {
        const displayName = activity.mainActor.displayName;
        if (!actorMap.has(displayName) || (activity.mainActor.inventoryId && !actorMap.get(displayName))) {
          actorMap.set(displayName, activity.mainActor.inventoryId || null);
        }
      }
      activity.relatedActors?.forEach((actor) => {
        const displayName = actor.displayName;
        if (!actorMap.has(displayName) || (actor.inventoryId && !actorMap.get(displayName))) {
          actorMap.set(displayName, actor.inventoryId || null);
        }
      });
    });

    const actorInventoryIds = (thread.actorNames ?? []).map((actorName) => actorMap.get(actorName) || null);

    return {
      id: thread.id,
      integrationId: '',
      name: thread.name,
      service: thread.service,
      clientApplication: clientApplication,
      actorNames: thread.actorNames ?? [],
      actorInventoryIds: actorInventoryIds,
      agentName: thread.agentNames[0],
      agentInventoryId: agentInventoryId,
      firstActivity: firstActivity,
      lastActivity: lastActivity,
      findingsBySeverity: thread.findingsBySeverity,
      findings: thread.findings.map((finding) => ({
        ...finding,
        severity: { [finding.rule.severity]: 1 } as Severities,
      })),
      activities: activities,
      ...this._mapFindingsByActivity(thread.findings),
    };
  }

  private _mapFindingsByActivity(findings: FindingModel[]): {
    findingsByActivity: Record<AgentStepModel['id'], Severities>;
    findingIdsByActivity: Record<AgentStepModel['id'], string[]>;
  } {
    const findingsPerActivity: Record<AgentStepModel['id'], Set<FindingModel['id']>> = {};
    const findingSeverities: Record<FindingModel['id'], FindingModel> = {};

    findings.forEach((finding) => {
      finding.relatedSteps.forEach((step) => {
        if (!findingsPerActivity[step.id]) {
          findingsPerActivity[step.id] = new Set<FindingModel['id']>();
        }

        findingsPerActivity[step.id].add(finding.id);
        findingSeverities[finding.id] = finding;
      });
    });

    const findingsByActivity: Record<AgentStepModel['id'], Severities> = {};
    const findingIdsByActivity: Record<AgentStepModel['id'], string[]> = {};

    Object.entries(findingsPerActivity).forEach(([activityId, findingIds]) => {
      findingIdsByActivity[activityId] = Array.from(findingIds);

      if (!findingsByActivity[activityId]) {
        findingsByActivity[activityId] = {} as Severities;
      }
      const findingId = findingIds.values().next().value;
      if (!findingId) {
        return;
      }
      const severity = findingSeverities[findingId].rule.severity;
      if (!severity) {
        return;
      }

      findingsByActivity[activityId][severity] = findingIds.size;
    });

    return { findingsByActivity, findingIdsByActivity };
  }
}
